/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import {
  HvInput,
  HvGrid,
  HvTypography,
  HvEmptyState,
  HvPagination,
} from "@hitachivantara/uikit-react-core";

import { SwitchWithLabels } from "components/form";
import {
  HvCellProps,
  useHvPagination,
  useHvSortBy,
  useHvTable,
  HvTable,
  HvTableContainer,
  HvTableHead,
  HvTableRow,
  HvTableHeader,
  HvTableBody,
  HvTableCell,
  hvTextColumn,
} from "@hitachivantara/uikit-react-lab";
import { Ban } from "@hitachivantara/uikit-react-icons";
import useStyles from "./styles";

const PositioningContent: React.FC = () => {
  const { t } = useTranslation("cameraAddEdit");
  const classes = useStyles();
  const {
    values,
    setFieldTouched,
    setFieldValue,
    status,
    setStatus,
    errors,
    touched,
  } = useFormikContext<CameraFormData>();

  // const { serverSideErrors = {} } = status;
  const DEFAULT_PAGE = 0;
  const DEFAULT_PAGE_SIZE = 10;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getFieldValue = useCallback((myObject: any, field: string) => {
    const value = field.split(".").reduce((obj, prop) => {
      if (prop.indexOf("[") >= 0 && prop.indexOf("]") > prop.indexOf("[")) {
        const index = +prop.substring(prop.indexOf("[") + 1, prop.indexOf("]"));
        return (
          obj &&
          obj[prop.substring(0, prop.indexOf("["))] &&
          obj[prop.substring(0, prop.indexOf("["))][index]
        );
      }
      return obj && obj[prop];
    }, myObject);
    return value;
  }, []);

  const parseStatusAzimuth = useCallback(
    (value: string | undefined) =>
      !value ||
      (value &&
        !Number.isNaN(value) &&
        Number(value) <= 360 &&
        Number(value) >= 0)
        ? "valid"
        : "invalid",
    []
  );

  const parseStatusMessageAzimuth = useCallback(
    (value: string | undefined) =>
      !value ||
      (value &&
        !Number.isNaN(value) &&
        Number(value) <= 360 &&
        Number(value) >= 0)
        ? ""
        : t("validation.azimuthOutOfRange"),
    [t]
  );

  const parseStatus = useCallback(
    (field: string) =>
      getFieldValue(status?.serverSideErrors ?? {}, field) ||
      (getFieldValue(errors, field) && getFieldValue(touched, field))
        ? "invalid"
        : "valid",
    [errors, getFieldValue, status?.serverSideErrors, touched]
  );

  const parseStatusMessage = useCallback(
    (field: string) => {
      if (getFieldValue(status?.serverSideErrors ?? {}, field)) {
        return getFieldValue(status?.serverSideErrors ?? {}, field);
      }

      return getFieldValue(errors, field) && getFieldValue(touched, field)
        ? getFieldValue(errors, field)
        : "";
    },
    [errors, getFieldValue, status?.serverSideErrors, touched]
  );

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );

  const columns = useMemo(() => {
    return [
      hvTextColumn({
        Header: t("positioning.columns.name") as string,
        accessor: "name",
        style: { width: "50%" },
      }),
      hvTextColumn({
        Header: t("positioning.columns.azimuth") as string,
        style: { width: "50%" },
        Cell: ({ row }: HvCellProps<PresetPositioningData>) => {
          return (
            <HvInput
              className={clsx(classes.root, classes.fields)}
              name={`azimuth${row.index}`}
              id={`azimuth${row.index}`}
              // label={t("details.fields.azimuth")}
              placeholder={t("positioning.fields.azimuthPlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              // required
              // eslint-disable-next-line no-underscore-dangle
              value={row.original.azimuth}
              disabled={!values.slewToCue}
              status={parseStatusAzimuth(row.original.azimuth)}
              statusMessage={parseStatusMessageAzimuth(row.original.azimuth)}
              onBlur={() => {
                setFieldTouched(
                  `presetsPositioning.${row.index}.azimuth`,
                  true,
                  false
                );
              }}
              onChange={(evt, value) => {
                setValueOnForm(
                  `presetsPositioning.${row.index}.azimuth`,
                  value
                );
              }}
            />
          );
        },
      }),
    ];
  }, [
    classes.fields,
    classes.root,
    parseStatusAzimuth,
    parseStatusMessageAzimuth,
    setFieldTouched,
    setValueOnForm,
    t,
    values.slewToCue,
  ]);

  const EmptyRow = () => (
    <HvTableRow>
      <HvTableCell colSpan={100} style={{ height: 100 }}>
        <HvEmptyState
          message="No data to display"
          icon={<Ban role="presentation" />}
        />
      </HvTableCell>
    </HvTableRow>
  );
  const [pageNr, setPage] = useState(DEFAULT_PAGE);
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headers,
    page,
    rows,
    getHvPaginationProps,
    state: { pageIndex, pageSize },
  } = useHvTable(
    {
      columns,
      data: values.presetsPositioning ?? [],
      /* defaultColumn: {
        Cell: ({ value }: any) => value ?? "—",
      }, */
      disableCreateExpandButton: true,
      initialState: { pageIndex: pageNr },
      disableSortRemove: true,
    },
    // useHvRowExpand,
    useHvSortBy,
    useHvPagination
  );

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  const itemNumber = useMemo(() => {
    return values.presetsPositioning?.length ?? 0;
  }, [values.presetsPositioning?.length]);
  const rowRenderer = (pages: any[]) => {
    return pages.map((row, index) => {
      prepareRow(row);

      return (
        <React.Fragment key={row.id}>
          <HvTableRow
            key={row.Header}
            {...row.getRowProps({
              "aria-rowindex": index,
            })}
          >
            {row.cells.map((cell: any) => (
              <HvTableCell key={cell.Header} {...cell.getCellProps()}>
                {cell.render("Cell")}
              </HvTableCell>
            ))}
          </HvTableRow>
          <HvTableRow style={{ display: row.isExpanded ? undefined : "none" }}>
            <HvTableCell
              style={{
                paddingBottom: 0,
                paddingTop: 0,
                textAlign: "center",
                height: 100,
              }}
              // colSpan="100%"
            >
              <HvTypography>
                Expanded content for: {row.values.name}
              </HvTypography>
            </HvTableCell>
          </HvTableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <HvGrid
        className={clsx(classes.root, classes.fields)}
        container
        item
        xs={12}
      >
        <HvGrid xs={12} md={6} item>
          <SwitchWithLabels
            className={classes.input}
            name="slewToCue"
            label={t("positioning.fields.slewToCue")}
            offLabel={t("positioning.fields.slewToCueOffLabel")}
            onLabel={t("positioning.fields.slewToCueOnLabel")}
            value="yes"
            checked={values.slewToCue || false}
            onChange={(evt, value) => {
              setFieldValue("slewToCue", value);
            }}
            onBlur={() => {
              setFieldTouched("slewToCue");
            }}
          />
        </HvGrid>
        <HvGrid item xs={12} md={3} />
        <HvGrid item xs={12} md={3}>
          <HvInput
            className={classes.input}
            name="altitude"
            label={t("positioning.fields.altitude")}
            placeholder={t("positioning.fields.altitudePlaceholder")}
            inputProps={{
              autoComplete: "off",
            }}
            value={values.altitude}
            disabled={!values.slewToCue}
            status={parseStatus("altitude")}
            statusMessage={parseStatusMessage("altitude")}
            onBlur={() => setFieldTouched("altitude", true, false)}
            onChange={(evt, value) => {
              setValueOnForm("altitude", value);
            }}
          />
        </HvGrid>
        <HvGrid item xs={12} md={12}>
          {/* <SimpleTable
            instance={instance}
            totalCount={instance.rows.length ?? 0}
            disableSelection
          /> */}

          {/*  <HvTable
            data={values.presetsPositioning ?? []}
            id="preset"
            columns={getColumns2}
            defaultPageSize={10}
            pageSize={
              (values.presetsPositioning?.length ?? 0) >
              pageSize2 * (pageIndex + 1)
                ? pageSize2
                : values.presetsPositioning?.length ?? 0
            }
            resizable={false}
            defaultSorted={defaultSorted}
            onPageSizeChange={onPageSizeChange}
            showPagination={(values.presetsPositioning?.length ?? 0) > 10}
          /> */}

          <HvTableContainer>
            <HvTable
              {...getTableProps({
                "aria-rowcount": itemNumber,
                caption: "Table Caption",
              } as Record<string, unknown>)}
            >
              <HvTableHead>
                <HvTableRow>
                  {headers.map((col) => (
                    <HvTableHeader
                      /* key={col.Header} */ {...col.getHeaderProps()}
                    >
                      {col.render("Header")}
                    </HvTableHeader>
                  ))}
                </HvTableRow>
              </HvTableHead>
              <HvTableBody {...getTableBodyProps()}>
                {page.length === 0 ? <EmptyRow /> : rowRenderer(page)}
              </HvTableBody>
            </HvTable>
          </HvTableContainer>
          {(pageSize !== DEFAULT_PAGE_SIZE ||
            (values.presetsPositioning?.length ?? 0) > pageSize) &&
            rows.length > 0 && <HvPagination {...getHvPaginationProps()} />}
        </HvGrid>
      </HvGrid>
    </>
  );
};

export default PositioningContent;
